import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

function App() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API}/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    }
  }, []);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'Todas') {
        params.append('category', selectedCategory);
      }
      
      const response = await fetch(`${API}/news?${params}`);
      
      // Verificar se a resposta foi bem-sucedida
      if (!response.ok) {
        console.error('Erro na API:', response.status);
        setNews([]);
        return;
      }
      
      const data = await response.json();
      
      // Garantir que data é um array
      if (Array.isArray(data)) {
        setNews(data);
      } else if (data.news && Array.isArray(data.news)) {
        setNews(data.news);
      } else {
        console.error('Formato de resposta inválido:', data);
        setNews([]);
      }
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // Proteger contra news não ser array
  const safeNews = Array.isArray(news) ? news : [];
  
  const filteredNews = safeNews.filter(item => 
    item.title && item.summary &&
    (item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.summary.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const featuredNews = filteredNews.filter(item => item.featured);
  const regularNews = filteredNews.filter(item => !item.featured);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-4xl font-bold">📰 NotíciasWeb</h1>
          <p className="text-blue-100 mt-2">Seu portal de notícias atualizado</p>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <input
            type="text"
            placeholder="🔍 Buscar notícias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 text-lg">Carregando notícias...</p>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-2xl text-gray-500">📭 Nenhuma notícia encontrada</p>
            <p className="text-gray-400 mt-2">Tente buscar por outro termo ou categoria</p>
          </div>
        ) : (
          <>
            {/* Featured News */}
            {featuredNews.length > 0 && (
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">✨ Em Destaque</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {featuredNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/noticia/${item.id}`}
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow block"
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-6">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-3">
                          {item.category}
                        </span>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 mb-4">{item.summary}</p>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>👤 {item.author}</span>
                          <span>📅 {new Date(item.date).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Regular News */}
            {regularNews.length > 0 && (
              <section>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">📰 Últimas Notícias</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {regularNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/noticia/${item.id}`}
                      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow block"
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-5">
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded mb-2">
                          {item.category}
                        </span>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-3">{item.summary}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{item.author}</span>
                          <span>{new Date(item.date).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-2">📰 NotíciasWeb</h3>
            <p className="text-gray-400">Seu portal de notícias atualizado</p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/sobre" className="text-gray-400 hover:text-white">Sobre</Link>
              <Link to="/privacidade" className="text-gray-400 hover:text-white">Privacidade</Link>
              <Link to="/termos" className="text-gray-400 hover:text-white">Termos</Link>
              <Link to="/contato" className="text-gray-400 hover:text-white">Contato</Link>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              © {new Date().getFullYear()} NotíciasWeb. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
